import '.d{display:flex;}.ssr-plugin'
import '.e{flex-direction:column;}.ssr-plugin'
import '.a8{align-items:stretch;}.ssr-plugin'
import '.du{box-shadow:0px 0px 15px 0px rgba(0, 0, 0, 0.15);}.ssr-plugin'
import '.am{border-radius:1.25rem;}.ssr-plugin'
import '.t{background-color:var(--palette-white);}.ssr-plugin'
import '.bq{overflow:hidden;}.ssr-plugin'
import '.er{text-overflow:ellipsis;}.ssr-plugin'
import '.ew{width:33rem;}.ssr-plugin'
import '.gx{height:50rem;}.ssr-plugin'
import { Button } from '@app/components/core/button/Button'
import { Title } from '@app/components/core/typography/Title'
import Row from '@app/components/layout/Row'
import { useTranslations } from '@i18n/useLanguage'
import { styled } from 'inlines'
import { useCallback, useMemo, useState } from 'react'
import Column from '@app/components/layout/Column'
import { TabsGroup } from '@app/components/core/tabs/Tabs'
import { Text } from '@app/components/core/typography/Text'
import { enrich } from '@i18n/localizeHelper'
import { match } from 'ts-pattern'
import { Recipient } from './RecipientModel'
import { isUserAdmin } from '@schema/utils'
import {
  INITIATIVE_USER_ROLES,
  type InitiativeUserRole,
} from '@schema/initiative'
import { Checkbox } from '@app/components/core/checkbox/Checkbox'
import { UserList } from '../userList/UserList'
import {
  CheckboxState,
  TriStateCheckbox,
} from '@app/components/core/checkbox/TriStateCheckbox'

export type Tab = 'admins' | 'members' | 'followers' | 'interestList'

const getDefaultGroupSelection = (groups: InitiativeUserRole[]): Tab => {
  if (groups.length === 0 || groups.includes('member')) {
    return 'members'
  }
  if (groups.some((role) => isUserAdmin({ role }))) {
    return 'admins'
  }
  if (groups.includes('applicant')) {
    return 'interestList'
  }

  return 'followers'
}

export const RecipientSelectionModal = ({
  recipients,
  selectedRecipientIds,
  onClose,
  onConfirm,
}: {
  recipients: Recipient[]
  selectedRecipientIds: number[]
  onClose: VoidFunction
  onConfirm: (selectedIds: number[]) => void
}) => {
  const viewedGroups = useMemo(
    () =>
      recipients
        .filter((r) => selectedRecipientIds.includes(r.id))
        .map((r) => r.role),
    [selectedRecipientIds, recipients],
  )

  const translations = useTranslations().app.dashboard.messages

  const [selectedRecipients, setSelectedRecipients] = useState<Set<number>>(
    new Set(selectedRecipientIds),
  )

  const [tab, setTab] = useState<Tab>(getDefaultGroupSelection(viewedGroups))

  const labelGroupText = match(tab)
    .with('admins', () => translations.admins)
    .with('members', () => translations.members)
    .with('followers', () => translations.followers)
    .with('interestList', () => translations.interestList)
    .exhaustive()
    .toLocaleLowerCase()

  const onConfirmSelection = useCallback(
    () => onConfirm([...selectedRecipients]),
    [selectedRecipients, onConfirm],
  )

  const onToggleRecipient = useCallback(
    (userId: number) => {
      setSelectedRecipients((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(userId)) {
          newSet.delete(userId)
        } else {
          newSet.add(userId)
        }
        return newSet
      })
    },
    [setSelectedRecipients],
  )

  const groupedRecipients = useMemo(() => {
    const admins = recipients.filter((r) => isUserAdmin(r))
    const members = recipients.filter(
      (r) => r.role === INITIATIVE_USER_ROLES.member,
    )
    const followers = recipients.filter(
      (r) => r.role === INITIATIVE_USER_ROLES.follower,
    )
    const interestList = recipients.filter(
      (r) => r.role === INITIATIVE_USER_ROLES.applicant,
    )
    return {
      admins,
      members,
      followers,
      interestList,
    }
  }, [recipients])

  const [selectedInGroup, selectAllCheckboxState] = useMemo(() => {
    const viewedGroup = groupedRecipients[tab]
    const selected = viewedGroup.filter((r) => selectedRecipients.has(r.id))
    const state = match<number, CheckboxState>(selected.length)
      .with(0, () => 'unchecked')
      .with(viewedGroup.length, () => 'checked')
      .otherwise(() => 'mixed')

    return [selected, state]
  }, [groupedRecipients, tab, selectedRecipients])

  const isSelectAllChecked = selectAllCheckboxState === 'checked'

  const selectAllToggle = () => {
    setSelectedRecipients((prev) => {
      const next = new Set(prev)
      if (isSelectAllChecked) {
        selectedInGroup.forEach((u) => next.delete(u.id))
      } else {
        groupedRecipients[tab].forEach((u) => next.add(u.id))
      }
      return next
    })
  }

  return (
    <div className="d e a8 du am t bq er ew gx"
    >
      <Row
        style={{
          padding: '1rem',
          background: 'var(--palette-white)',
          borderBottom: '1px solid var(--gray-100)',
        }}
      >
        <Title.H5>{translations.selectRecipients}</Title.H5>
      </Row>
      <Column
        alignItems="stretch"
        style={{ paddingInline: '1rem', overflowY: 'auto' }}
      >
        <TabsGroup
          selectedTab={tab}
          onSelectTab={setTab}
          style={{
            alignSelf: 'center',
          }}
          tabItemStyle={{ padding: '1.25rem 1rem' }}
          tabs={[
            { value: 'admins', label: translations.admins },
            { value: 'members', label: translations.members },
            { value: 'followers', label: translations.followers },
            { value: 'interestList', label: translations.interestList },
          ]}
        />
        <Row
          style={{
            paddingBottom: '1rem',
            justifyContent: 'space-between',
          }}
        >
          <Text.Small>
            {enrich(
              translations.numberOfMembersLabel,
              `${groupedRecipients[tab].length} ${labelGroupText}`,
            )}
          </Text.Small>
          <TriStateCheckbox
            label={
              isSelectAllChecked
                ? translations.deselectAll
                : translations.selectAll
            }
            labelPosition="left"
            labelStyle={{
              color: 'var(--palette-blue-400)',
              fontSize: '0.875rem',
            }}
            state={selectAllCheckboxState}
            onClick={selectAllToggle}
          />
        </Row>
        <UserList
          users={groupedRecipients[tab].map((u) => ({
            id: u.id,
            name: u.name,
            avatar: u.avatar,
            action: (
              <Checkbox
                selected={selectedRecipients.has(u.id)}
                onClick={() => onToggleRecipient(u.id)}
              />
            ),
          }))}
          style={{
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        />
      </Column>
      <Row
        gap={'0.5rem'}
        style={{
          display: 'flex',
          padding: '1rem 1.5rem',
          justifyContent: 'flex-end',
          alignItems: 'center',
          borderTop: '1px solid var(--gray-100)',
        }}
      >
        <Button
          size="small"
          variant="secondary"
          label={translations.back}
          onClick={onClose}
          style={{ flexShrink: 0 }}
        />
        <Button
          size="small"
          label={translations.done}
          onClick={onConfirmSelection}
          style={{ flexShrink: 0 }}
        />
      </Row>
    </div>
  )
}
